import type { WorkflowKind } from '@fruuit/shared'
import { Badge, Callout, Checkbox, SkeletonText } from '@fruuit/ui'
import { useState } from 'react'
import { errorMessage } from '../../api/client'
import { useRecord } from './api'
import type { FieldSpec } from './form'
import { ASSESSMENT_FIELDS, DIALOGUE_FIELDS, MILESTONE_FIELDS } from './specs'

const DIFF_FIELDS: Record<WorkflowKind, readonly { name: string; label: string }[]> = {
  assessments: [
    ...ASSESSMENT_FIELDS.filter((field: FieldSpec) => field.name !== 'client_org_id'),
    { name: 'incident_ids', label: 'Incidents' },
    { name: 'source_ids', label: 'Sources' },
  ],
  milestones: MILESTONE_FIELDS,
  dialogues: DIALOGUE_FIELDS,
}

export interface FieldChange {
  name: string
  label: string
  before: string
  after: string
  changed: boolean
}

function show(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) return value.length ? [...value].sort().join(', ') : '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

/** Field-by-field comparison of two versions of the same record. */
export function diffRecords(
  kind: WorkflowKind,
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): FieldChange[] {
  return DIFF_FIELDS[kind].map((field) => {
    const a = show(before[field.name])
    const b = show(after[field.name])
    return { name: field.name, label: field.label, before: a, after: b, changed: a !== b }
  })
}

export interface VersionDiffProps {
  kind: WorkflowKind
  /** The earlier (corrected) version. */
  previousId: string
  /** The correcting version. */
  currentId: string
}

/** Diff of a correction against the version it corrects (S13). */
export function VersionDiff({ kind, previousId, currentId }: VersionDiffProps) {
  const previous = useRecord(kind, previousId)
  const current = useRecord(kind, currentId)
  const [showAll, setShowAll] = useState(false)
  if (previous.isError || current.isError) {
    return (
      <Callout tone="danger" role="alert">
        {errorMessage(previous.error ?? current.error)}
      </Callout>
    )
  }
  if (!previous.data || !current.data) return <SkeletonText lines={5} />
  const changes = diffRecords(
    kind,
    previous.data as unknown as Record<string, unknown>,
    current.data as unknown as Record<string, unknown>,
  )
  const changed = changes.filter((change) => change.changed)
  const rows = showAll ? changes : changed
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-soft">
          Version {previous.data.version} → version {current.data.version}:{' '}
          <strong className="text-ink">
            {changed.length} {changed.length === 1 ? 'field' : 'fields'} changed
          </strong>
        </p>
        <Checkbox
          label="Show unchanged fields"
          checked={showAll}
          onChange={(event) => setShowAll(event.target.checked)}
        />
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-sage">No field differs between these versions.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">Changes between versions</caption>
            <thead className="bg-[#f5f8f3] text-xs tracking-[0.06em] text-[#5f7268] uppercase">
              <tr>
                <th scope="col" className="px-3 py-2">
                  Field
                </th>
                <th scope="col" className="px-3 py-2">
                  v{previous.data.version}
                </th>
                <th scope="col" className="px-3 py-2">
                  v{current.data.version}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.name} className="border-t border-line align-top">
                  <th scope="row" className="px-3 py-2.5 font-semibold whitespace-nowrap text-ink">
                    {row.label}
                    {row.changed ? (
                      <Badge tone="watch" className="ml-2">
                        Changed
                      </Badge>
                    ) : null}
                  </th>
                  <td
                    className={`px-3 py-2.5 whitespace-pre-line ${row.changed ? 'bg-attention-soft/40 text-ink-soft line-through decoration-attention/40' : 'text-ink-soft'}`}
                  >
                    {row.before}
                  </td>
                  <td
                    className={`px-3 py-2.5 whitespace-pre-line ${row.changed ? 'bg-positive-soft/50 text-ink' : 'text-ink-soft'}`}
                  >
                    {row.after}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
